import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, ErrorState, PageHeader, PageLoader, Select, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { WorkerMultiSelect } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { PRIORITY_OPTIONS } from '../../../constants/status';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { quotationService } from '../services/quotationService';
import type { FormEvent } from 'react';

/** Accepted quotation -> service job (labour + material list copied over). */
export default function QuotationConvert() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { form, set } = useForm({ scheduledDate: toISODate(new Date()), assignedTo: [], priority: 'normal' });
  const { data: q, error, reload } = useFetch(() => quotationService.get(id), [id]);
  useCrumb(`/quotations/${id}`, q?.quotationNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!q) return <PageLoader />;
  if (q.status !== 'approved') return <ErrorState error={{ message: 'Only accepted quotations can be turned into a job. Mark it accepted first.' }} />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = await quotationService.convert(id, {
        scheduledDate: form.scheduledDate || undefined,
        assignedTo: form.assignedTo.length ? form.assignedTo : undefined,
        priority: form.priority,
      });
      toast.success(`Job ${data.serviceNo} created`);
      navigate(`/jobs/${data._id}`, { replace: true });
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(`/quotations/${id}`)} disabled={saving}>Cancel</Button>
      <Button type="submit" form="convert-form" leftIcon="wrench" loading={saving}>Create job</Button>
    </>
  );

  return (
    <>
      <PageHeader title="Create job from quotation" subtitle={`${q.quotationNo} · ${q.customer?.name}`} actions={actions} />
      <form id="convert-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Schedule" subtitle="Labour and the full material list are copied to the job">
                <DatePicker label="Visit date" value={form.scheduledDate} onChange={set('scheduledDate')} />
                <Select label="Priority" options={PRIORITY_OPTIONS} value={form.priority} onChange={set('priority')} searchable={false} />
                <WorkerMultiSelect value={form.assignedTo} onChange={set('assignedTo')} containerClassName="sm:col-span-2" hint="Leave empty to assign later" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="From quotation" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Job', q.title || '-'],
                    ['Labour lines', q.labourItems.length],
                    ['Material lines', q.materialItems.length],
                    ['Quoted total', money(q.grandTotal), { strong: true }],
                  ]}
                />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
